import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  VStack, 
  HStack, 
  Text, 
  Button, 
  Flex, 
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  useToast,
  Badge,
  Heading,
  Grid,
  GridItem,
  Icon,
  Divider,
  Tooltip,
  useColorModeValue,
  Wrap,
  WrapItem,
  Image
} from '@chakra-ui/react';
import { keyframes, css } from '@emotion/react';
import { motion } from 'framer-motion';
import { 
  FaCoins, 
  FaDice, 
  FaRedo, 
  FaVolumeUp, 
  FaVolumeMute, 
  FaRegChartBar,
  FaHistory,
  FaInfoCircle,
  FaCircle,
  FaGem,
  FaStar,
  FaCrown,
  FaSquare,
  FaAppleAlt,
  FaLemon,
  FaGripfire,
  FaBolt, 
  FaRocket, 
  FaFire, 
  FaTrophy, 
  FaChartLine, 
  FaQuestion,
  FaHeart,
} from 'react-icons/fa';
import { GiCherry, GiGrapes, GiLemon, GiStrawberry, GiDiamonds, GiSevenPointedStar, GiTwoCoins, GiWatermelon } from 'react-icons/gi';

// Define symbols with their properties based on 20 Super Hot slot machine
const symbols = [
  { id: 'cherry', icon: FaAppleAlt, color: 'red.500', weight: 10, payout: { 3: 10, 4: 20, 5: 100 } },
  { id: 'lemon', icon: FaLemon, color: 'yellow.400', weight: 10, payout: { 3: 10, 4: 20, 5: 100 } },
  { id: 'orange', icon: FaCircle, color: 'orange.400', weight: 10, payout: { 3: 10, 4: 20, 5: 100 } },
  { id: 'plum', icon: FaAppleAlt, color: 'purple.500', weight: 8, payout: { 3: 20, 4: 50, 5: 200 } },
  { id: 'watermelon', icon: FaCircle, color: 'green.500', weight: 8, payout: { 3: 20, 4: 50, 5: 200 } },
  { id: 'grape', icon: FaGripfire, color: 'purple.300', weight: 6, payout: { 3: 30, 4: 100, 5: 400 } },
  { id: 'seven', icon: FaGem, color: 'red.500', weight: 4, payout: { 3: 40, 4: 400, 5: 1000 }, isWild: true },
  { id: 'star', icon: FaStar, color: 'yellow.400', weight: 2, payout: { 3: 40, 4: 400, 5: 10000 }, isScatter: true },
];

// Animation for spinning reels
const spinAnimation = (reelIndex) => {
  return keyframes`
    0% { transform: translateY(0); }
    100% { transform: translateY(-2100px); }
  `;
};

// CSS for spinning animations - faster for more immediate feedback
const spinningReelStyles = css`
  .spinning-reel-0 { animation: ${spinAnimation(0)} 2.5s linear infinite; }
  .spinning-reel-1 { animation: ${spinAnimation(1)} 2.6s linear infinite; }
  .spinning-reel-2 { animation: ${spinAnimation(2)} 2.7s linear infinite; }
  .spinning-reel-3 { animation: ${spinAnimation(3)} 2.8s linear infinite; }
  .spinning-reel-4 { animation: ${spinAnimation(4)} 2.9s linear infinite; }
`;

// Paylines configuration - 20 lines exactly like in 20 Super Hot slot machine
const paylines = [
  [1, 1, 1, 1, 1], // Line 1: Middle row
  [0, 0, 0, 0, 0], // Line 2: Top row
  [2, 2, 2, 2, 2], // Line 3: Bottom row
  [0, 1, 2, 1, 0], // Line 4: V shape
  [2, 1, 0, 1, 2], // Line 5: Inverted V
  [0, 0, 1, 2, 2], // Line 6: Diagonal top-left to bottom-right
  [2, 2, 1, 0, 0], // Line 7: Diagonal bottom-left to top-right
  [1, 0, 0, 0, 1], // Line 8: U shape on top
  [1, 2, 2, 2, 1], // Line 9: U shape on bottom
  [0, 1, 1, 1, 0], // Line 10: U shape in middle top
  [2, 1, 1, 1, 2], // Line 11: U shape in middle bottom
  [1, 1, 0, 1, 1], // Line 12: Middle with top center
  [1, 1, 2, 1, 1], // Line 13: Middle with bottom center
  [0, 1, 0, 1, 0], // Line 14: Zigzag top
  [2, 1, 2, 1, 2], // Line 15: Zigzag bottom
  [0, 0, 1, 0, 0], // Line 16: Top row with middle dip
  [2, 2, 1, 2, 2], // Line 17: Bottom row with middle rise
  [0, 1, 1, 1, 0], // Line 18: Top corners to middle
  [2, 1, 1, 1, 2], // Line 19: Bottom corners to middle
  [1, 0, 0, 0, 1], // Line 20: Middle to top and back
];

// Reel component
const Reel = ({ symbols, spinning, stopReel, finalSymbols, reelIndex, spinDuration }) => {
  const reelRef = useRef(null);
  const symbolHeight = 116; // Fixed height for each symbol - increased for taller grid
  
  // Fixed set of symbols for this reel - 3 visible + extras for animation
  const [reelSymbols, setReelSymbols] = useState([]);
  

  
  // Pre-generate symbols for faster spinning start
  useEffect(() => {
    // Pre-generate symbols when component mounts
    if (reelSymbols.length === 0) {
      const initialSymbols = [];
      for (let i = 0; i < 9; i++) {
        initialSymbols.push(symbols[Math.floor(Math.random() * symbols.length)]);
      }
      setReelSymbols(initialSymbols);
    }
  }, []);
  
  // Update reel symbols when spinning starts
  useEffect(() => {
    if (spinning) {
      // Keep the current visible symbols but add new ones for the spin
      const currentVisible = reelSymbols.slice(0, 3);
      const newSymbols = [];
      
      // Add current visible symbols at the bottom of the new reel
      newSymbols.push(...currentVisible);
      
      // Add random symbols above for spinning
      for (let i = 0; i < 15; i++) { // More symbols for longer animation
        newSymbols.push(symbols[Math.floor(Math.random() * symbols.length)]);
      }
      
      // Add the final symbols that will be shown after spinning
      const finalSymbolsForReel = [];
      for (let i = 0; i < 3; i++) { // 3 final symbols
        const symbolIndex = (finalSymbols[reelIndex] + i) % symbols.length;
        finalSymbolsForReel.push(symbols[symbolIndex]);
      }
      
      newSymbols.push(...finalSymbolsForReel);
      setReelSymbols(newSymbols);
    }
  }, [spinning]);
  
  // Animation style for spinning
  const spinStyle = {
    transform: spinning 
      ? 'translateY(0)' // Start position
      : `translateY(-${(reelSymbols.length - 3) * symbolHeight}px)`, // End position showing final 3 symbols
    transition: spinning 
      ? 'none' 
      : `transform ${1 + reelIndex * 0.2}s cubic-bezier(0.33, 1, 0.68, 1)`,
  };
  
  return (
    <Box
      ref={reelRef}
      h="350px" // Even taller for better visibility
      overflow="hidden"
      position="relative"
      borderRadius="md"
      bg="rgba(0, 30, 15, 0.8)" // Dark green background matching sidebar theme
      border="2px solid #c9a030" // Golden border
      boxShadow="inset 0 0 10px rgba(0, 230, 118, 0.15)"
      w="100%"
    >
      <Box
        position="absolute"
        width="100%"
        style={spinning ? {} : spinStyle} // Only apply transition when stopping
        className={spinning ? `spinning-reel-${reelIndex}` : ''}
      >
        {reelSymbols.map((symbol, index) => (
          <Flex
            key={index}
            h={`${symbolHeight}px`}
            align="center"
            justify="center"
            bg="transparent"
          >
            <Icon 
              as={symbol.icon} 
              color={symbol.color} 
              boxSize={12} // Larger icons
              filter="drop-shadow(0 0 3px rgba(255, 255, 255, 0.7))" // Glow effect
            />
          </Flex>
        ))}
      </Box>
      
      {/* Grid lines to create 3 equal sections */}
      <Box
        position="absolute"
        top="33.33%"
        left={0}
        right={0}
        height="1px"
        bg="rgba(0, 230, 118, 0.3)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        top="66.66%"
        left={0}
        right={0}
        height="1px"
        bg="rgba(0, 230, 118, 0.3)"
        pointerEvents="none"
      />
      
      {/* Overlay for better visibility of current symbols */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        pointerEvents="none"
        bgGradient="linear(to-b, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 10%, rgba(0,0,0,0) 90%, rgba(0,0,0,0.5) 100%)"
      />
    </Box>
  );
};

// Payline visualization
const PaylineIndicator = ({ payline, active, winningLine }) => {
  const lineColor = winningLine ? "green.400" : "gray.500";
  const opacity = active ? 1 : 0.3;
  
  return (
    <Flex justify="space-between" opacity={opacity} mb={1}>
      {payline.map((pos, idx) => (
        <Box 
          key={idx} 
          w="12px" 
          h="12px" 
          borderRadius="full" 
          bg={lineColor}
          boxShadow={winningLine ? "0 0 10px #48BB78" : "none"}
        />
      ))}
    </Flex>
  );
};

// Main SlotMachine component
const SlotMachine = () => {
  const [spinning, setSpinning] = useState(false);
  const [reels, setReels] = useState(Array(5).fill(0)); // Current position of each reel
  const [balance, setBalance] = useState(10000); // Starting balance
  const [betAmount, setBetAmount] = useState(20); // Default bet amount (20 Super Hot uses 20, 40, 100, 200, 400)
  const [activePaylines, setActivePaylines] = useState(20); // Fixed 20 paylines like in 20 Super Hot
  const [winAmount, setWinAmount] = useState(0); // Amount won in last spin
  const [winningLines, setWinningLines] = useState([]); // Winning paylines
  const [autoSpin, setAutoSpin] = useState(false); // Auto spin mode
  const [spinCount, setSpinCount] = useState(0); // Number of spins
  const [muted, setMuted] = useState(true); // Audio muted by default
  const [lastResults, setLastResults] = useState([]); // Last 5 spin results
  const toast = useToast();
  
  // Generate random positions for a reel
  function generateRandomReelPositions() {
    return Math.floor(Math.random() * symbols.length);
  }
  
  // Get symbol at a specific position in the reel
  const getSymbolAt = (reelIndex, position) => {
    const symbolIndex = (reels[reelIndex] + position) % symbols.length;
    return symbols[symbolIndex];
  };
  
  // Check for wins based on 20 Super Hot mechanics
  const checkWins = () => {
    let totalWin = 0;
    const winningLines = [];
    
    // In 20 Super Hot, there are exactly 20 fixed paylines
    // All wins must start from the leftmost reel
    
    // Check each of the 20 paylines
    for (let i = 0; i < 20; i++) {
      const payline = paylines[i];
      const symbolsOnLine = [];
      
      // Get symbols on this payline
      for (let j = 0; j < 5; j++) {
        const rowIndex = payline[j];
        const symbolIndex = (reels[j] + rowIndex) % symbols.length;
        symbolsOnLine.push(symbols[symbolIndex]);
      }
      
      // Check for winning combinations (must start from leftmost reel)
      const firstSymbol = symbolsOnLine[0];
      
      // Skip if first symbol is a scatter (scatters pay anywhere, not on lines)
      if (firstSymbol.isScatter) continue;
      
      let count = 1;
      let winningSymbol = firstSymbol;
      
      // Count matching symbols from left to right
      for (let j = 1; j < 5; j++) {
        const currentSymbol = symbolsOnLine[j];
        
        // In 20 Super Hot, the wild symbol (7) substitutes for any regular symbol
        // The chain breaks if we don't have a match or wild
        if (currentSymbol.isWild || firstSymbol.isWild || currentSymbol.id === firstSymbol.id) {
          count++;
          
          // If we have a wild and a regular symbol, use the regular symbol for payout
          if (currentSymbol.isWild && !winningSymbol.isWild) {
            // Keep the current winning symbol
          } else if (firstSymbol.isWild && !currentSymbol.isWild && !currentSymbol.isScatter) {
            winningSymbol = currentSymbol; // Use the regular symbol for payout
          }
        } else {
          // Break the chain if no match
          break;
        }
      }
      
      // In 20 Super Hot, you need at least 3 matching symbols for a win
      if (count >= 3) {
        if (winningSymbol.payout && winningSymbol.payout[count]) {
          const win = winningSymbol.payout[count] * betAmount;
          totalWin += win;
          winningLines.push({ payline: i + 1, count, symbol: winningSymbol, win });
        }
      }
    }
    
    // In 20 Super Hot, scatter symbols (stars) pay anywhere
    // They don't need to be on a payline
    const scatterSymbol = symbols.find(s => s.isScatter);
    let scatterCount = 0;
    let scatterPositions = [];
    
    // Check each position on the grid for scatters
    for (let reel = 0; reel < 5; reel++) {
      for (let row = 0; row < 3; row++) {
        const symbolIndex = (reels[reel] + row) % symbols.length;
        if (symbols[symbolIndex].isScatter) {
          scatterCount++;
          scatterPositions.push({ reel, row });
        }
      }
    }
    
    // In 20 Super Hot, you need at least 3 scatters for a win
    if (scatterCount >= 3 && scatterSymbol) {
      const scatterWin = scatterSymbol.payout[scatterCount] * betAmount;
      totalWin += scatterWin;
      winningLines.push({ 
        payline: 'Scatter', 
        count: scatterCount, 
        symbol: scatterSymbol, 
        win: scatterWin, 
        isScatter: true,
        positions: scatterPositions
      });
    }
    
    return { totalWin, winningLines };
  };
  
  // Spin the reels
  // Pre-generate random positions for faster response
  const [preGeneratedPositions, setPreGeneratedPositions] = useState(Array(5).fill(null).map(() => generateRandomReelPositions()));

  // Pre-generate next positions when idle
  useEffect(() => {
    if (!spinning) {
      setPreGeneratedPositions(Array(5).fill(null).map(() => generateRandomReelPositions()));
    }
  }, [spinning]);

  const spin = () => {
    if (spinning) return;
    
    // Check if player has enough balance
    const totalBet = betAmount * activePaylines;
    if (balance < totalBet) {
      toast({
        title: "Insufficient balance",
        description: "You don't have enough JINU to place this bet",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Start spinning immediately using pre-generated positions
    setSpinning(true);
    setWinAmount(0);
    setWinningLines([]);
    setSpinCount(prev => prev + 1);
    
    // Deduct bet amount
    setBalance(prev => prev - totalBet);
    
    // Use pre-generated positions for immediate response
    setReels(preGeneratedPositions);
    
    // Set a timer to stop the spinning
    const spinDuration = 1500; // 1.5 seconds of spinning
    
    // Stop reels one by one with a delay
    const baseDelay = spinDuration; // Base delay before first reel stops
    const reelStopInterval = 300; // Interval between each reel stopping
    
    // Stop the spinning animation after the delay
    setTimeout(() => {
      setSpinning(false);
      
      // Check for wins after all reels have stopped
      setTimeout(() => {
        const { totalWin, winningLines } = checkWins();
        
        // Check for random jackpot (20 Super Hot feature)
        const jackpotWin = checkRandomJackpot();
        const totalWinWithJackpot = totalWin + jackpotWin;
        
        setWinAmount(totalWinWithJackpot);
        setWinningLines(winningLines);
        setBalance(prev => prev + totalWinWithJackpot);
        
        // Add to results history
        setLastResults(prev => {
          const newResults = [...prev, { bet: totalBet, win: totalWinWithJackpot }];
          return newResults.slice(-5); // Keep last 5 results
        });
        
        // Show win notification as popup
        if (totalWinWithJackpot > 0) {
          // Create a custom win popup that appears for 1 second
          const winPopup = document.createElement('div');
          winPopup.style.position = 'fixed';
          winPopup.style.top = '50%';
          winPopup.style.left = '50%';
          winPopup.style.transform = 'translate(-50%, -50%)';
          winPopup.style.backgroundColor = 'rgba(0, 230, 118, 0.9)';
          winPopup.style.color = 'white';
          winPopup.style.padding = '20px 40px';
          winPopup.style.borderRadius = '10px';
          winPopup.style.fontSize = '24px';
          winPopup.style.fontWeight = 'bold';
          winPopup.style.zIndex = '9999';
          winPopup.style.boxShadow = '0 0 20px rgba(0, 230, 118, 0.5)';
          winPopup.style.textAlign = 'center';
          winPopup.innerHTML = `WIN! ${totalWinWithJackpot.toLocaleString()} JINU`;
          
          document.body.appendChild(winPopup);
          
          // Remove the popup after 1 second
          setTimeout(() => {
            document.body.removeChild(winPopup);
          }, 1000);
        }
        
        // Offer gamble feature for wins (20 Super Hot feature)
        if (totalWin > 0 && !jackpotWin) {
          // In a real implementation, we would show a gamble button here
          // For simplicity, we're just showing a notification
          toast({
            title: "Gamble Feature Available",
            description: "In the real 20 Super Hot slot, you could gamble your winnings for a chance to double them!",
            status: "info",
            duration: 3000,
            isClosable: true,
          });
        }
        
        // Continue auto-spin if enabled
        if (autoSpin) {
          setTimeout(() => spin(), 1000);
        }
      }, 800); // Wait a bit after the reels stop
    }, baseDelay + (4 * reelStopInterval)); // Wait for all reels to stop
  };
  
  // Calculate RTP (Return to Player) - 20 Super Hot has 95.79% RTP
  const calculateRTP = () => {
    if (lastResults.length === 0) return "95.79%"; // Default RTP for 20 Super Hot
    
    const totalBet = lastResults.reduce((sum, result) => sum + result.bet, 0);
    const totalWin = lastResults.reduce((sum, result) => sum + result.win, 0);
    
    if (totalBet === 0) return "95.79%";
    
    const rtp = (totalWin / totalBet) * 100;
    return `${rtp.toFixed(2)}%`;
  };
  
  // Chance for random jackpot feature (simulating 20 Super Hot's random jackpot)
  const checkRandomJackpot = () => {
    // Small chance (0.1%) to trigger the jackpot feature
    if (Math.random() < 0.001) {
      // Determine jackpot level (4 levels in 20 Super Hot)
      const jackpotLevel = Math.floor(Math.random() * 4) + 1;
      const jackpotAmount = jackpotLevel * 500 * betAmount; // Simplified calculation
      
      toast({
        title: "Jackpot Cards Mystery!",
        description: `Congratulations! You won the Level ${jackpotLevel} jackpot of ${jackpotAmount.toLocaleString()} JINU!`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      
      setBalance(prev => prev + jackpotAmount);
      return jackpotAmount;
    }
    
    return 0;
  };
  
  return (
    <Box>
      <VStack spacing={4} align="stretch">
        {/* Removed sound and auto-spin controls to make more space for the grid */}
        
        {/* Main slot machine display */}
        <Box
          bg="rgba(0, 30, 15, 0.9)" // Dark green background matching sidebar theme
          borderRadius="md"
          p={4}
          boxShadow="0 10px 30px -10px rgba(0, 0, 0, 0.7)"
          position="relative"
          overflow="hidden"
          border="4px solid rgba(0, 230, 118, 0.3)" // Border matching sidebar divider
        >
          
          {/* Payline indicators on the sides */}
          <Flex>
            {/* Left side payline numbers */}
            <VStack 
              spacing={0} 
              mr={2} 
              bg="rgba(0, 230, 118, 0.2)" // Matching sidebar badge background
              borderRadius="md"
              p={1}
              color="gold"
              fontWeight="bold"
              fontSize="sm"
              align="center"
              justify="space-between"
              h="350px" // Increased height to match the grid
            >
              <Text>20</Text>
              <Text>15</Text>
              <Text>10</Text>
              <Text>5</Text>
              <Text>1</Text>
            </VStack>
            
            {/* Reels */}
            <Box flex="1">
              <Grid templateColumns="repeat(5, 1fr)" gap={1}>
                {Array(5).fill(null).map((_, i) => (
                  <GridItem key={i}>
                    <Reel 
                      symbols={symbols} 
                      spinning={spinning} 
                      finalSymbols={reels}
                      reelIndex={i}
                      spinDuration={3}
                    />
                  </GridItem>
                ))}
              </Grid>
            </Box>
            
            {/* Right side payline numbers */}
            <VStack 
              spacing={0} 
              ml={2} 
              bg="rgba(0, 230, 118, 0.2)" // Matching sidebar badge background
              borderRadius="md"
              p={1}
              color="gold"
              fontWeight="bold"
              fontSize="sm"
              align="center"
              justify="space-between"
              h="350px" // Increased height to match the grid
            >
              <Text>20</Text>
              <Text>15</Text>
              <Text>10</Text>
              <Text>5</Text>
              <Text>1</Text>
            </VStack>
          </Flex>
          
          {/* Win display removed from grid and converted to popup */}
          
        </Box>
        
        {/* Controls outside the game grid */}
        <HStack mt={3} spacing={4} justify="space-between">
          {/* Bet amount controls */}
          <Flex flex="3">
            <Wrap spacing={2}>
              {[20, 40, 100, 200, 400].map((betValue, idx) => (
                <WrapItem key={idx}>
                  <Button 
                    size="sm"
                    colorScheme={betAmount === betValue ? "green" : "gray"}
                    onClick={() => setBetAmount(betValue)}
                    isDisabled={spinning}
                    bg={betAmount === betValue ? "rgba(0, 230, 118, 0.8)" : "rgba(0, 180, 90, 0.5)"}
                    _hover={{ bg: betAmount === betValue ? "rgba(0, 250, 130, 0.9)" : "rgba(0, 200, 100, 0.6)" }}
                    color="white"
                    fontWeight="bold"
                    borderRadius="md"
                    boxShadow="0 2px 4px rgba(0, 230, 118, 0.2)"
                  >
                    {betValue}
                  </Button>
                </WrapItem>
              ))}
            </Wrap>
          </Flex>
          
          {/* Spin button */}
          <Button
            size="md"
            flex="1"
            onClick={spin}
            isLoading={spinning}
            loadingText="Spinning..."
            bg="linear-gradient(135deg, rgba(0, 180, 90, 0.8) 0%, rgba(0, 230, 118, 0.9) 100%)"
            _hover={{
              bg: "linear-gradient(135deg, rgba(0, 200, 100, 0.9) 0%, rgba(0, 250, 130, 1) 100%)"
            }}
            boxShadow="0 4px 12px rgba(0, 230, 118, 0.25)"
            color="white"
            fontWeight="bold"
          >
            {autoSpin ? `AUTO SPIN (${spinCount})` : 'SPIN'}
          </Button>
        </HStack>
        
        {/* Stats and info */}
        <Grid templateColumns="repeat(3, 1fr)" gap={4}>
          <GridItem>
            <Box p={3} bg="rgba(0, 230, 118, 0.1)" borderRadius="md" borderColor="rgba(0, 230, 118, 0.3)" borderWidth="1px">
              <Text color="#00e676" fontSize="sm">Total Bet</Text>
              <Text fontWeight="bold" color="white">{(betAmount * activePaylines).toLocaleString()} JINU</Text>
            </Box>
          </GridItem>
          
          <GridItem>
            <Box p={3} bg="rgba(0, 230, 118, 0.1)" borderRadius="md" borderColor="rgba(0, 230, 118, 0.3)" borderWidth="1px">
              <Text color="#00e676" fontSize="sm">RTP</Text>
              <Text fontWeight="bold" color="white">{calculateRTP()}</Text>
            </Box>
          </GridItem>
          
          <GridItem>
            <Box p={3} bg="rgba(0, 230, 118, 0.1)" borderRadius="md" borderColor="rgba(0, 230, 118, 0.3)" borderWidth="1px">
              <Text color="#00e676" fontSize="sm">Max Win</Text>
              <Text fontWeight="bold" color="white">{(betAmount * 10000).toLocaleString()} JINU</Text>
            </Box>
          </GridItem>
        </Grid>
        
        {/* Paytable */}
        <Box mt={4}>
          <Heading size="sm" mb={2} color="white">Paytable</Heading>
          <Grid templateColumns="repeat(3, 1fr)" gap={2}>
            {symbols.map(symbol => (
              <Box 
                key={symbol.id} 
                p={2} 
                bg="rgba(0, 0, 0, 0.2)" 
                borderRadius="md"
                display="flex"
                alignItems="center"
              >
                <Icon as={symbol.icon} color={symbol.color} boxSize={6} mr={2} />
                <Box>
                  <Text fontSize="xs" color="gray.400">
                    {symbol.isWild ? 'WILD' : symbol.id.charAt(0).toUpperCase() + symbol.id.slice(1)}
                  </Text>
                  <Text fontSize="xs" color="gold">
                    x3: {symbol.payout[3]}x | x5: {symbol.payout[5]}x
                  </Text>
                </Box>
              </Box>
            ))}
          </Grid>
        </Box>
      </VStack>
    </Box>
  );
};

export default SlotMachine;
